"use client";
import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { convertIntoString } from '@/services/constantService';
import ArticleFilter from './ArticleFilter';
import { getArticleList } from '@/services/publicService';
import { getAllCategories } from '@/services/createArticleService';

function getVisiblePages(current, last, width = 5) {
  if (last <= width) {
    return Array.from({ length: last }, (_, i) => i + 1);
  }
  const half = Math.floor(width / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(last, start + width - 1);
  if (end - start < width - 1) {
    start = Math.max(1, end - width + 1);
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function truncateByWords(text, maxWords = 16) {
  const plainText = String(text || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!plainText) return '';

  const words = plainText.split(' ');
  if (words.length <= maxWords) {
    return plainText;
  }

  return `${words.slice(0, maxWords).join(' ')}...`;
}

const ArticlesContent = ({ blogs, pagination }) => {
  // Ensure blogs & pagination have safe initial values
  const [filteredBlogs, setFilteredBlogs] = useState(Array.isArray(blogs) ? blogs : []);
  const [blogPagination, setBlogPagination] = useState(pagination || {});
  const [filterValue, setFilterValue] = useState("All");
  const [currentPage, setCurrentPage] = useState(pagination?.current_page || 1);
  const [searchValue, setSearchValue] = useState("");
  const router = useRouter();
  const [options, setOptions] = useState([]);
  const totalPages = blogPagination?.last_page || 1;
  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, totalPages, 5),
    [currentPage, totalPages]
  );
  const totalRecords =
    blogPagination?.total_record ??
    blogPagination?.total ??
    filteredBlogs.length;
 
  useEffect(() => {
    const apiCurrentPage = Number(blogPagination?.current_page);
    if (Number.isFinite(apiCurrentPage) && apiCurrentPage > 0 && apiCurrentPage !== currentPage) {
      setCurrentPage(apiCurrentPage);
      return;
    }
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [blogPagination?.current_page, currentPage, totalPages]);


  const handlePageChange = async (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    await getArticleListFun(pageNumber);
    // document.getElementById('publications-section')?.scrollIntoView({ behavior: 'smooth' }); // Optional: Scroll to top of section
  };

  const getArticleListFun = async (pageNumber) => {
    const articleData = await getArticleList({ page: pageNumber, search: searchValue, category: filterValue });
    // Support both structured response { data, pagination } and plain arrays
    const blogsFromAPI = Array.isArray(articleData?.data)
      ? articleData.data
      : Array.isArray(articleData)
        ? articleData
        : [];

    setFilteredBlogs(blogsFromAPI);

    if (articleData?.pagination) {
      setBlogPagination(articleData.pagination);
    }
  }

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    if (page === currentPage) return;
    handlePageChange(page);
  };

   // Get All Category
   const handleCategoryList = async () => {
    try {
        const response = await getAllCategories();
        const transformedOptions = transformCategoriesToOptions(response.data);
        setOptions(transformedOptions);
    } catch (error) {
        console.log('Error:', error);
    }
   }

      // Transform API data to react-select format
  const transformCategoriesToOptions = (categories) => {
    return categories.map(category => ({
      value: category.id,
      label: category.name
    }));
  };
useEffect(() => {
  handleCategoryList();
}, []);

  const handleSearch = () => {
    getArticleListFun(1);
  }

  const getCardDescription = (blog) => {
    const takeaways = blog?.takeaways;

    if (Array.isArray(takeaways)) {
      return truncateByWords(takeaways.join(' '));
    }

    return truncateByWords(takeaways || '');
  };

  return (
    <>
      <div className="recent-blogs-header-bp">
        <div className="row align-items-center">
          <div className="col-lg-4">
            <h2 className="recent-blogs-title-bp">Our Recent Articles</h2>
          </div>
          <div className="col-lg-8">
            {/* <ArticleFilter filterValue={filterValue} setFilterValue={setFilterValue} />  */}
            <div className="recent-blogs-filter-wrapper-bp gap-3">
              
              <div className="search-input-wrapper-bp">
                <input type="text" placeholder="Search by Article titles, Author name, Categories..." value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
                <svg className="search-icon-bp" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 15L21 21" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <select 
                className="recent-blogs-filter-bp"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
              >
                <option value="All">All Categories</option>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <button className="recent-blogs-filter-button-bp" onClick={handleSearch}>Search</button>
            </div>
          </div>
        </div>
      </div>

      <div className="recent-blogs-grid-bp">
        <div className="row">
          {filteredBlogs.length > 0 ? (
              filteredBlogs.map((blog, index) => (
                <div key={blog?.id || index} className="col-lg-4 col-md-6 mb-4">  
                  <div className="blog-card-bp">
                    <div className="blog-card-image-wrapper-bp">
                      <div className="blog-card-image-bp">
                        <Image 
                          src={blog?.image?.url && typeof blog.image.url === 'string' && blog.image.url.trim() ? blog.image.url : "/icons/hero.png"} 
                          alt={blog?.title || "Blog image"}
                          width={400}
                          height={250}
                          className="img-fluid"
                        />
                      </div>
                    </div>
                    <div className="blog-card-content-bp">
                      <h3 className="blog-card-title-bp"><Link href={`/article/${blog?.slug || ''}`} target='_blank' prefetch={false} style={{textDecoration: 'none', color: '#0C0C0C'}}>{convertIntoString(blog?.title || '')}</Link></h3>
                      <p className="blog-card-description-bp">{getCardDescription(blog)}</p>
                      <div className="blog-card-meta-bp">
                        <Link href={`/author/${blog?.author?.slug || ''}`} target='_blank' className="blog-card-author-bp" style={{textDecoration: 'none', color: '#0C0C0C'}}>{blog?.author?.name || 'Unknown'}</Link>
                        <span className="blog-card-separator-bp">|</span>
                        <span className="blog-card-date-bp">{blog?.created_at || ''}</span>
                      </div>
                      <Link href={`/article/${blog?.slug || ''}`} className="blog-card-button-bp" style={{textDecoration: 'none', color: '#0C0C0C'}}>
                        Read More
                        <svg width="29" height="24" viewBox="0 0 29 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath={`url(#clip0_readmore_card_${blog?.id ?? index})`}>
                            <path d="M15.695 16.83L20.5 12L15.695 7.16998C15.6516 7.11299 15.5965 7.066 15.5333 7.03217C15.4702 6.99834 15.4005 6.97848 15.329 6.97392C15.2576 6.96935 15.1859 6.98021 15.119 7.00573C15.0521 7.03126 14.9914 7.07087 14.9411 7.12188C14.8909 7.17289 14.8521 7.23411 14.8275 7.30139C14.803 7.36867 14.7931 7.44045 14.7987 7.51186C14.8043 7.58327 14.8252 7.65265 14.8599 7.7153C14.8946 7.77795 14.9424 7.8324 15 7.87498L18.595 11.5L9.03 11.5C8.89739 11.5 8.77022 11.5527 8.67645 11.6464C8.58268 11.7402 8.53 11.8674 8.53 12C8.53 12.1326 8.58268 12.2598 8.67645 12.3535C8.77022 12.4473 8.89739 12.5 9.03 12.5L18.595 12.5L15 16.125C14.9065 16.2191 14.8543 16.3466 14.8547 16.4792C14.8552 16.6119 14.9083 16.739 15.0025 16.8325C15.0967 16.926 15.2241 16.9782 15.3568 16.9778C15.4894 16.9773 15.6165 16.9241 15.71 16.83H15.695Z"/>
                          </g>
                          <rect x="26" y="2.5" width="19" height="23" rx="9.5" transform="rotate(90 26 2.5)"/>
                          <defs>
                            <clipPath id={`clip0_readmore_card_${blog?.id ?? index}`}>
                              <rect x="25.5" y="3" width="18" height="22" rx="9" transform="rotate(90 25.5 3)" fill="white"/>
                            </clipPath>
                          </defs>
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              ))
          ) : (
            <div className="col-12 text-center py-5">
              <p>No blogs found.</p>
            </div>
          )}
        </div>
        {/* Pagination Controls */}
        

        {totalRecords > 1 && totalPages > 1 && (
                <div className="pagination-container">
                    <button
                        type="button"
                        className="pag-icon-btn"
                        onClick={() => goToPage(1)}
                        disabled={currentPage <= 1}
                        aria-label="First page"
                    >
                        <ChevronsLeft size={20} strokeWidth={1.75} />
                    </button>
                    <button
                        type="button"
                        className="pag-box-btn pag-box-btn--prev"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        aria-label="Previous page"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    {visiblePages.map((page) => (
                        <button
                            key={page}
                            type="button"
                            className={`pag-num ${currentPage === page ? 'is-active' : ''}`}
                            onClick={() => goToPage(page)}
                            disabled={currentPage === page}
                            aria-label={`Page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        type="button"
                        className="pag-box-btn pag-box-btn--next"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        aria-label="Next page"
                    >
                        <ChevronRight size={18} />
                    </button>
                    <button
                        type="button"
                        className="pag-icon-btn pag-icon-btn--emphasis"
                        onClick={() => goToPage(totalPages)}
                        disabled={currentPage >= totalPages}
                        aria-label="Last page"
                    >
                        <ChevronsRight size={20} strokeWidth={1.75} />
                    </button>
                </div>
            )}
      </div>
    </>
  );
};

export default ArticlesContent;

